import store from '@/store'

// 總後台操作權限（角色帳號有任一則登入後進總後台）
const BACKEND_OPERATIONS = [
	'OPERATION_STORE_EXPIRING_30_DAYS',
	'OPERATION_BACKEND_STORE_MANAGEMENT',
	'OPERATION_SALES_MANAGEMENT',
	'OPERATION_CONTRACT_MANAGEMENT',
	'OPERATION_ANNOUNCEMENT_MANAGEMENT',
	'OPERATION_MAIL_HISTORY',
	'OPERATION_SYSTEM_SETTING',
]

// 店家前後台使用權限
const STORE_OPERATIONS = [
	'OPERATION_POS_SYSTEM',
	'OPERATION_ORDER_MANAGEMENT',
	'OPERATION_REPORT',
	'OPERATION_MENU_MANAGEMENT',
	'OPERATION_STORE_MANAGEMENT',
	'OPERATION_WHATS_EAT_COUPON',
]

/** 是否有任一總後台操作權限 */
export const hasAnyBackendOperation = permissions => {
	return (
		Array.isArray(permissions) &&
		BACKEND_OPERATIONS.some(op => permissions.includes(op))
	)
}

/** 是否有任一家店前後台操作權限 */
export const hasAnyStoreOperation = permissions => {
	return (
		Array.isArray(permissions) &&
		STORE_OPERATIONS.some(op => permissions.includes(op))
	)
}

/** 總後台路徑對應的 OPERATION（用於依權限過濾選單項目） */
export const PATH_TO_BACKEND_OPERATION = {
	'nearly-expired': 'OPERATION_STORE_EXPIRING_30_DAYS',
	'store-manage': 'OPERATION_BACKEND_STORE_MANAGEMENT',
	'business-manage': 'OPERATION_SALES_MANAGEMENT',
	contractManage: 'OPERATION_CONTRACT_MANAGEMENT',
	announcementManager: 'OPERATION_ANNOUNCEMENT_MANAGEMENT',
	'mail-history': 'OPERATION_MAIL_HISTORY',
	'system-setting': 'OPERATION_SYSTEM_SETTING',
}

/** 店家前後台路徑對應的 OPERATION（角色帳號選完店家後，左側選單只顯示店家前後台且依權限過濾） */
export const PATH_TO_STORE_OPERATION = {
	'store-pos': 'OPERATION_POS_SYSTEM',
	'order-manage': 'OPERATION_ORDER_MANAGEMENT',
	report: 'OPERATION_REPORT',
	'menu-manage': 'OPERATION_MENU_MANAGEMENT',
	'account-manage': 'OPERATION_STORE_MANAGEMENT',
	'whats-eat-manage': 'OPERATION_WHATS_EAT_COUPON',
}

// 總後台：依擁有權限順序決定登入後第一頁
const BACKEND_OPERATION_TO_PATH = [
	['OPERATION_STORE_EXPIRING_30_DAYS', '/nearly-expired'],
	['OPERATION_BACKEND_STORE_MANAGEMENT', '/store-manage'],
	['OPERATION_SALES_MANAGEMENT', '/business-manage'],
	['OPERATION_CONTRACT_MANAGEMENT', '/contractManage/contract-classification'],
	['OPERATION_ANNOUNCEMENT_MANAGEMENT', '/announcementManager/announcement'],
	['OPERATION_MAIL_HISTORY', '/mail-history'],
	['OPERATION_SYSTEM_SETTING', '/system-setting'],
]

// 店家前後台：依順序決定登入後第一頁（僅在無總後台權限時使用）
const STORE_OPERATION_TO_PATH = [
	['OPERATION_POS_SYSTEM', '/store-pos'],
	['OPERATION_ORDER_MANAGEMENT', '/order-manage/socket'],
	['OPERATION_REPORT', '/report/order-list'],
	['OPERATION_MENU_MANAGEMENT', '/menu-manage/meals'],
	['OPERATION_STORE_MANAGEMENT', '/account-manage/store-set'],
	['OPERATION_WHATS_EAT_COUPON', '/whats-eat-manage/whats-eat-panel'],
]

/**
 * 依 permissionList 取得角色帳號（ROLE_XXX）登入後預設路徑
 * 有任一總後台權限 → 總後台並依順序取第一個符合的頁面；否則依店家權限順序
 */
export const getDefaultPathByPermissions = permissions => {
	if (!Array.isArray(permissions)) return null
	const hasBackend = BACKEND_OPERATIONS.some(op => permissions.includes(op))
	if (hasBackend) {
		for (const [op, path] of BACKEND_OPERATION_TO_PATH) {
			if (permissions.includes(op)) return path
		}
	}
	for (const [op, path] of STORE_OPERATION_TO_PATH) {
		if (permissions.includes(op)) return path
	}
	return null
}

/**
 * 依 permissionList 取得角色帳號選取店家後的預設路徑（僅店家前後台）
 * 用於 ROLE_XXX 選店家時，避免導向總後台路徑導致權限不符
 */
export const getDefaultPathByStorePermissions = permissions => {
	if (!Array.isArray(permissions)) return null
	for (const [op, path] of STORE_OPERATION_TO_PATH) {
		if (permissions.includes(op)) return path
	}
	return null
}

const usePermission = () => {
	let _permission = {}
	const updateUserStorage = data => {
		const {
			id,
			account,
			name,
			phone,
			email,
			loginTime,
			buyerType,
			loginType,
			permissionList,
			storeList,
			role,
			manager,
			superManager,
			notManager,
			sales,
		} = data

		const isRoleAccount = role === true
		const storeName = storeList[0]?.name
		document.title = isRoleAccount
			? '點溡點餐平台'
			: storeName
			? storeName
			: '點溡點餐平台'
		store.commit('storage/setToken', loginType === 'BACK')
		store.commit('storage/setBuyerType', buyerType)
		store.commit('storage/setUserId', id)
		store.commit('storage/setAccount', account)
		store.commit('storage/setStoreList', storeList)
		if (!isRoleAccount) {
			store.commit('storage/setStoreId', storeList[0]?.id)
			store.commit('storage/setStoreName', storeName)
			store.commit('storage/setLoginStoreId', storeList[0]?.id)
		} else {
			store.commit('storage/setStoreId', null)
			store.commit('storage/setStoreName', '')
			store.commit('storage/setLoginStoreId', null)
		}

		store.commit('storage/setPermissions', permissionList)
		store.commit('storage/setRole', role)
		store.commit('storage/setManager', manager || sales)
		store.commit('storage/setSuper', superManager)
	}

	const loginRole = () => {
		let permissions = store.state.storage.permissions
		const rolePermission = Array.isArray(permissions)
			? permissions.find(v => typeof v === 'string' && v.startsWith('ROLE_'))
			: null
		if (permissions.find(v => v === 'USER_SUPER_MANAGER')) {
			store.commit('storage/setLoginRole', 'USER_MANAGER')
			store.commit('storage/setSwitchRole', 'USER_MANAGER')
		} else if (permissions.find(v => v === 'USER_MANAGER')) {
			store.commit('storage/setLoginRole', 'USER_MANAGER')
			store.commit('storage/setSwitchRole', 'USER_MANAGER')
		} else if (permissions.find(v => v === 'USER_SALES_PERSON')) {
			store.commit('storage/setLoginRole', 'USER_SALES_PERSON')
			store.commit('storage/setSwitchRole', 'USER_SALES_PERSON')
		} else if (permissions.find(v => v === 'USER_STORE')) {
			store.commit('storage/setLoginRole', 'USER_STORE')
			store.commit('storage/setSwitchRole', 'USER_STORE')
		} else if (rolePermission) {
			store.commit('storage/setLoginRole', rolePermission)
			store.commit('storage/setSwitchRole', rolePermission)
		}
	}

	return {
		set() {
			const storagePermission = store.state.storage.permissions
			if (storagePermission) {
				const permissionArr = storagePermission
				_permission = {}
				permissionArr.forEach(e => {
					_permission[e] = true
				})
			}
		},
		get() {
			return _permission
		},
		/**
		 * 檢查是否有權限
		 * @param {*} key string 權限名稱
		 */
		is(key) {
			return _permission[key] || false
		},
		every(...keys) {
			return keys.every(e => _permission[e])
		},
		some(...keys) {
			return keys.some(e => _permission[e])
		},
		updateUserStorage,
		loginRole,
		getDefaultPathByPermissions,
		getDefaultPathByStorePermissions,
		hasAnyBackendOperation,
		hasAnyStoreOperation,
	}
}

export default Vue => {
	Vue.prototype.$permission = usePermission()
}
